import { useForm } from "react-hook-form";
import { useState, useEffect, useRef } from "react";
import { getApi } from '../../providers/AuthContext';
import { Link, useHistory } from "react-router-dom";
import { motion } from 'framer-motion';
import { Save, ArrowLeft, User, FileText, Loader2 } from 'lucide-react';

import * as yup from 'yup';
import { yupResolver } from "@hookform/resolvers/yup";

import { PapInput, PapTextArea, PapImageUpload, PapErrors } from '../FormElements';

import { defaultImage } from '../../settings/config';

interface ChildFormProps {
  child?: any;
}

const ChildForm: React.FC<ChildFormProps> = ({ child }) => {
  const schema = yup
    .object()
    .shape({
      first_name: yup.string().required('First name is required').label('First Name'),
      bio: yup.string().optional().label('Bio'),
      image_id: yup.string().optional().nullable().label('Image'),
    });

  const history = useHistory();
  const api = getApi();

  const [pageErrors, setPageErrors] = useState<string[]>([]);
  const [imageUrl, setImageUrl] = useState(defaultImage);

  const formRef = useRef<HTMLFormElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: yupResolver(schema)
  });

  const resetPage = () => {
    reset();
    setPageErrors([]);
    setImageUrl(defaultImage);
  };

  useEffect(() => {
    if (child) {
      if (child.image_url) {
        setImageUrl(child.image_url);
      }
      reset(child);
    }
  }, [child]);

  const cancel = () => {
    resetPage();
  };

  const saveChild = async (data: any) => {
    const child_data = {
      child: data
    };
    let result = null;
    try {
      if (child && child.id) {
        result = await api.put(`/api/v1/children/${child.id}`, child_data);
      } else {
        result = await api.post('/api/v1/children', child_data);
      }
    } catch (ex: any) {
      setPageErrors([ex.response?.error || 'An error occurred']);
    }

    if (result) {
      resetPage();
      history.push('/children');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="form-container"
    >
      <div className="form-wrapper">
        {/* Form card */}
        <div className="bg-card border border-border/40 rounded-2xl shadow-soft-sm overflow-hidden">
          {/* Accent bar */}
          <div className="h-1.5 bg-gradient-to-r from-primary via-accent to-secondary" />

          <div className="p-6 sm:p-8">
            {/* Form header */}
            <div className="flex items-center gap-3 mb-6 pb-5 border-b border-border/30">
              <div className="w-10 h-10 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">
                  {child ? 'Edit Profile' : 'New Child Profile'}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {child ? 'Update your child\'s information' : 'Create a profile for your child'}
                </p>
              </div>
            </div>

            <PapErrors errors={pageErrors} />
            <form ref={formRef} noValidate onSubmit={handleSubmit(saveChild)}>
              {/* Image upload centered */}
              <div className="flex flex-col items-center mb-8">
                <PapImageUpload formBinding={register("image_id")} setFormValue={setValue as (name: string, value: any) => void} imageUrlState={[imageUrl, setImageUrl]} />
              </div>

              {/* Form fields with section label */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Details</span>
                </div>
                <PapInput formBinding={register("first_name")} required={true} fieldLabel="First Name" fieldName="first_name" error={errors.first_name} />
                <PapTextArea formBinding={register("bio")} fieldLabel="Bio" fieldName="bio" placeholder="Write something about your child..." error={errors.bio} />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 mt-2 border-t border-border/30">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-tertiary py-2.5 px-6 rounded-xl gap-2 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {isSubmitting ? 'Saving...' : 'Save'}
                </button>
                <Link
                  to="/children"
                  onClick={cancel}
                  className="btn-light py-2.5 px-5 rounded-xl no-underline text-inherit gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Go Back
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ChildForm;
